import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, IconButton, Tooltip,
  CircularProgress, Chip,
} from '@mui/material';
import {
  EditNote as EditNoteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { updateWorkPackage } from 'shared/utils/proposalApi';
import { axiosService } from 'shared/helpers/axios';
import SlateEditor from 'components/social/profile/reflections/SlateEditor';

const timeAgo = (dateStr) => {
  if (!dateStr) return null;
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const AUTOSAVE_DELAY = 2000;

export default function WpNotesDialog({ open, onClose, wp, onSaved }) {
  const [notes, setNotes] = useState('');
  const [comments, setComments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const autosaveTimer = useRef(null);
  const latestNotes = useRef('');

  // Load notes and comments when dialog opens
  useEffect(() => {
    if (!open || !wp) return;
    const initial = wp.notes || '';
    setNotes(initial);
    latestNotes.current = initial;
    setDirty(false);
    setSavedAt(wp.updated || null);

    axiosService.get('/proposal/wp-note-comments/', { params: { work_package: wp.id } })
      .then((res) => setComments(res.data?.results ?? res.data ?? []))
      .catch(() => setComments([]));

    return () => clearTimeout(autosaveTimer.current);
  }, [open, wp]);

  const persist = useCallback(async (value) => {
    if (!wp?.id) return;
    setSaving(true);
    const result = await updateWorkPackage(wp.id, { notes: value });
    setSaving(false);
    if (result.success) {
      setSavedAt(new Date().toISOString());
      setDirty(false);
      onSaved?.(result.data);
    }
  }, [wp, onSaved]);

  const handleChange = useCallback((md) => {
    setNotes(md);
    latestNotes.current = md;
    setDirty(true);
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => persist(latestNotes.current), AUTOSAVE_DELAY);
  }, [persist]);

  const handleSaveNow = () => {
    clearTimeout(autosaveTimer.current);
    persist(latestNotes.current);
  };

  const handleClose = () => {
    clearTimeout(autosaveTimer.current);
    if (dirty) persist(latestNotes.current);
    onClose();
  };

  // --- Comment callbacks ---

  const handleAddComment = useCallback(async ({ commentId, body }) => {
    if (!wp?.id) return;
    try {
      const res = await axiosService.post('/proposal/wp-note-comments/', {
        work_package: wp.id,
        comment_id: commentId,
        body,
      });
      setComments((prev) => [...prev, res.data]);
    } catch (_) {}
  }, [wp]);

  const handleResolveComment = useCallback(async (commentId) => {
    const comment = comments.find((c) => String(c.comment_id) === String(commentId));
    if (!comment) return;
    try {
      const res = await axiosService.patch(`/proposal/wp-note-comments/${comment.id}/resolve/`);
      setComments((prev) =>
        prev.map((c) => (c.id === comment.id ? { ...c, resolved: res.data.resolved } : c))
      );
    } catch (_) {}
  }, [comments]);

  const handleReplyComment = useCallback(async (commentId, body) => {
    const comment = comments.find((c) => String(c.comment_id) === String(commentId));
    if (!comment) return;
    try {
      const res = await axiosService.post(`/proposal/wp-note-comments/${comment.id}/reply/`, { body });
      setComments((prev) =>
        prev.map((c) =>
          c.id === comment.id ? { ...c, replies: [...(c.replies || []), res.data] } : c
        )
      );
    } catch (_) {}
  }, [comments]);

  // Stable key so SlateEditor re-initialises when the WP changes
  const editorKey = useMemo(() => (wp?.id || 'no-wp'), [wp?.id]);

  if (!wp) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      sx={{zIndex: 1600 }}
      PaperProps={{
        sx: { height: '85vh', display: 'flex', flexDirection: 'column',
           
         }
      }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditNoteIcon fontSize="small" color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600} lineHeight={1.2}>
              WP{wp.number} Notes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {wp.title} — shared notebook for WP members
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexShrink: 0 }}>
          {saving ? (
            <Chip
              icon={<CircularProgress size={11} />}
              label="Saving…"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          ) : savedAt ? (
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
              label={`Saved ${timeAgo(savedAt)}`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          ) : null}
          <Tooltip title="Save now">
            <span>
              <IconButton size="small" onClick={handleSaveNow} disabled={saving || !dirty}>
                <SaveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
        <SlateEditor
          key={editorKey}
          value={notes}
          onChange={handleChange}
          placeholder={`Use this space for WP${wp.number} working notes — ideas, decisions, open questions, meeting summaries.\n\nHighlight any text to add a comment. Auto-saves after 2 s of inactivity.`}
          height={600}
          richText
          comments={comments}
          onAddComment={handleAddComment}
          onResolveComment={handleResolveComment}
          onReplyComment={handleReplyComment}
        />
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={handleClose} variant="contained" size="small">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
